// AnswerProgressOnDemandWithCohorts.js
import React, { useRef, useState } from "react";
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../api/firebase";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
  Grid,
} from "@mui/material";

/* ── 並列プール（同時実行制限） ── */
const promisePool = async ({ items, worker, concurrency = 5, onTick, isCancelled }) => {
  const results = new Array(items.length);
  let index = 0;
  const runner = async () => {
    while (true) {
      if (isCancelled?.()) throw new Error("cancelled");
      const cur = index++;
      if (cur >= items.length) break;
      results[cur] = await worker(items[cur], cur);
      onTick && onTick(cur);
    }
  };
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, runner);
  await Promise.all(workers);
  return results;
};

/* ── ヒストグラム（0–100% を 10%刻み, 11ビン：0,1–10,…,91–99,100） ── */
const makeHistogram = (ratios) => {
  const bins = Array.from({ length: 11 }, () => 0);
  ratios.forEach((r) => {
    const idx = Math.min(10, Math.floor(r * 10 + 1e-9)); // 1.0 → 10
    bins[idx] += 1;
  });
  const labels = [
    "0%",
    "1–10%",
    "11–20%",
    "21–30%",
    "31–40%",
    "41–50%",
    "51–60%",
    "61–70%",
    "71–80%",
    "81–90%",
    "100%",
  ];
  return { bins, labels, max: Math.max(1, ...bins) };
};

const percentile = (arr, p) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const idx = Math.min(s.length - 1, Math.max(0, Math.floor((p / 100) * (s.length - 1))));
  return s[idx];
};

/* ── 共通：コホート統計を作る ── */
const summarizeCohort = (rows, B) => {
  const n = rows.length;
  const sumA = rows.reduce((acc, r) => acc + r.A, 0);
  const required = n * B;
  const overallRate = required > 0 ? sumA / required : 0;

  const ratios = rows.map((r) => r.ratio);
  const ratiosPurpose = rows.filter((r) => r.isPurposeTarget).map((r) => r.ratio);

  return {
    n,
    sumA,
    required,
    overallRate,
    avgRatio: ratios.reduce((a, v) => a + v, 0) / (ratios.length || 1),
    medRatio: percentile(ratios, 50),
    p90: percentile(ratios, 90),
    p10: percentile(ratios, 10),
    histAll: makeHistogram(ratios),
    histPurpose: makeHistogram(ratiosPurpose),
    purposeCount: ratiosPurpose.length,
  };
};

const AnswerProgressOnDemandWithCohorts = () => {
  const [rows, setRows] = useState([]); // {uid, name, email, A, B, ratio, isPurposeTarget, cohort}
  const [B, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // 集計結果（全体／新規／持ち越し）
  const [summaryAll, setSummaryAll] = useState(null);
  const [summaryNew, setSummaryNew] = useState(null);
  const [summaryCarry, setSummaryCarry] = useState(null);

  const cancelRef = useRef(false);

  const reset = () => {
    setRows([]);
    setB(null);
    setMsg("");
    setProgress({ done: 0, total: 0 });
    setSummaryAll(null);
    setSummaryNew(null);
    setSummaryCarry(null);
    cancelRef.current = false;
  };

  // B（公開済み）= openDate <= now
  const fetchOpenCountB = async () => {
    const now = new Date();
    const qB = query(collection(db, "yaeNoInori"), where("openDate", "<=", now));
    const snap = await getCountFromServer(qB);
    return snap.data().count;
  };

  // users 全件ページング
  const fetchAllUsersPaged = async (pageSize = 500) => {
    const usersCol = collection(db, "users");
    const all = [];
    let last = null;

    while (true) {
      if (cancelRef.current) throw new Error("cancelled");
      const qUsers = last
        ? query(usersCol, orderBy("__name__"), startAfter(last), limit(pageSize))
        : query(usersCol, orderBy("__name__"), limit(pageSize));

      const snap = await getDocs(qUsers);
      if (snap.empty) break;

      snap.forEach((d) => all.push({ id: d.id, ...d.data() }));
      last = snap.docs[snap.docs.length - 1];
      if (snap.size < pageSize) break;
    }
    return all;
  };

  // 各ユーザの A を count
  const getAnswerCountForUser = async (uid) => {
    const subCol = collection(db, `users/${uid}/yaeChikaiAnswers`);
    const countSnap = await getCountFromServer(subCol);
    return countSnap.data().count;
  };

  const handleRun = async () => {
    try {
      reset();
      setBusy(true);
      setMsg("公開済み数（B）を取得中…");
      const b = await fetchOpenCountB();
      setB(b);

      setMsg("users をページング取得中…");
      const users = await fetchAllUsersPaged(500);

      // 対象：payments に "3" が含まれる（paymentPhase === "3"）
      const targets = users.filter((u) => {
        const pays = Array.isArray(u.payments) ? u.payments : [];
        return pays.some((p) => p && p.paymentPhase === "3");
      });

      setProgress({ done: 0, total: targets.length });
      setMsg(`回答件数（A）を集計中… 0 / ${targets.length}`);

      const PURPOSE_VALUE = "音源視聴 & グループ活動";

      const results = await promisePool({
        items: targets,
        concurrency: 5,
        isCancelled: () => cancelRef.current,
        onTick: () =>
          setProgress((prev) => {
            const next = { ...prev, done: prev.done + 1 };
            setMsg(`回答件数（A）を集計中… ${next.done} / ${next.total}`);
            return next;
          }),
        worker: async (u) => {
          const pays = Array.isArray(u.payments) ? u.payments : [];
          const A = await getAnswerCountForUser(u.id);
          const ratio = b > 0 ? A / b : 0;

          // コホート判定
          let cohort = "carry"; // デフォルトを持ち越しに
          if (pays.length === 1) cohort = "new"; // 新規（3期から）
          // （length === 0 はそもそも targets に入らない想定）

          return {
            uid: u.id,
            name: u.displayName || u.name || u.registrationName || "(no name)",
            email: u.email || "",
            A,
            B: b,
            ratio,
            isPurposeTarget: u?.purpose2025SecondHalf === PURPOSE_VALUE,
            cohort, // "new" | "carry"
          };
        },
      });

      results.sort((a, b2) => b2.ratio - a.ratio);
      setRows(results);

      // サマリ作成（全体／新規／持ち越し）
      const allRows = results;
      const newRows = results.filter((r) => r.cohort === "new");
      const carryRows = results.filter((r) => r.cohort === "carry");

      setSummaryAll(summarizeCohort(allRows, b));
      setSummaryNew(summarizeCohort(newRows, b));
      setSummaryCarry(summarizeCohort(carryRows, b));

      setMsg(`完了（対象 ${targets.length} 人 | 新規 ${newRows.length} / 持ち越し ${carryRows.length}）`);
    } catch (e) {
      setMsg(e.message === "cancelled" ? "中断しました。" : `エラー：${e.message}`);
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    cancelRef.current = true;
  };

  /* ── 小さなカードコンポーネント：統計＋ヒスト ── */
  const CohortCard = ({ title, summary, colorKey = "primary" }) => {
    if (!summary) return null;
    const maxScale = summary.histAll.max;

    return (
      <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
        <Typography variant="subtitle1" gutterBottom>{title}</Typography>

        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, summary.overallRate * 100)}
          />
        </Box>

        <Typography variant="body2" sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <span><strong>{(summary.overallRate * 100).toFixed(1)}%</strong></span>
          <span>（合計A: {summary.sumA.toLocaleString()} / 必要: {summary.required.toLocaleString()}）</span>
          <span>人数: {summary.n.toLocaleString()} 人</span>
          <span>目的一致: {summary.purposeCount.toLocaleString()} 人</span>
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={`平均 ${(summary.avgRatio*100).toFixed(1)}%`} />
          <Chip size="small" label={`中央値 ${(summary.medRatio*100).toFixed(1)}%`} />
          <Chip size="small" label={`P90 ${(summary.p90*100).toFixed(1)}%`} />
          <Chip size="small" label={`P10 ${(summary.p10*100).toFixed(1)}%`} />
        </Stack>

        {/* ヒストグラム：塗り=コホート / 枠線=目的一致 */}
        <Box sx={{
          mt: 2,
          position: "relative",
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          height: 160,
          borderLeft: "1px solid #ddd",
          borderBottom: "1px solid #ddd",
          pl: 1,
          pr: 1,
          pb: 1,
        }}>
          {summary.histAll.bins.map((countAll, i) => {
            const countPurpose = summary.histPurpose.bins[i] || 0;
            const hAll = (countAll / maxScale) * 135;
            const hPur = (countPurpose / maxScale) * 135;

            return (
              <Box key={i} sx={{ position: "relative", width: 18, height: 135 }}>
                <Tooltip title={`${summary.histAll.labels[i]}：${countAll}人`} arrow>
                  <Box sx={{
                    position: "absolute",
                    bottom: 0, left: 0, right: 0,
                    height: Math.max(2, hAll),
                    bgcolor: `${colorKey}.main`,
                    opacity: 0.85,
                    borderRadius: 0.5,
                    transition: "height .2s",
                  }} />
                </Tooltip>
                {countPurpose > 0 && (
                  <Tooltip title={`${summary.histAll.labels[i]}（目的）：${countPurpose}人`} arrow>
                    <Box sx={{
                      position: "absolute",
                      bottom: 0, left: 0, right: 0,
                      height: Math.max(2, hPur),
                      border: "2px solid",
                      borderColor: "secondary.main",
                      borderRadius: 0.5,
                      boxSizing: "border-box",
                      background: "transparent",
                      pointerEvents: "none",
                    }} />
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, pr: 1 }}>
          {summary.histAll.labels.map((lbl, i) => (
            <Typography key={i} variant="caption" sx={{ width: 18, textAlign: "center" }}>
              {i % 2 === 0 ? lbl.replace("%", "") : ""}
            </Typography>
          ))}
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Chip size="small" label="コホート全体（塗り）" sx={{ bgcolor: `${colorKey}.main`, color: "white" }} />
          <Chip size="small" label="音源視聴 & グループ活動（枠）" sx={{ border: "1px solid", borderColor: "secondary.main", bgcolor: "transparent" }} />
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* 操作バー */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }}>
        <Button variant="contained" onClick={handleRun} disabled={busy}>集計</Button>
        <Button variant="outlined" color="warning" onClick={() => (cancelRef.current = true)} disabled={!busy}>停止</Button>
        {busy && <CircularProgress size={20} />}
        <Typography variant="body2" color="text.secondary">{msg}</Typography>
        {B != null && <Chip size="small" label={`公開済み（B）：${B} / 88`} />}
      </Stack>

      {/* 概要カード群：全体 / 新規 / 持ち越し */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={12}>
          <CohortCard title="全体の回答率" summary={summaryAll} colorKey="primary" />
        </Grid>
        <Grid item xs={12} md={6}>
          <CohortCard title="新規（3期から：payments.length === 1）" summary={summaryNew} colorKey="success" />
        </Grid>
        <Grid item xs={12} md={6}>
          <CohortCard title="持ち越し（過去から）：payments.length > 1" summary={summaryCarry} colorKey="info" />
        </Grid>
      </Grid>

      {/* 進捗バー（処理中のみ） */}
      {progress.total > 0 && busy && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (progress.done / progress.total) * 100)}
          />
          <Typography variant="caption" color="text.secondary">
            {progress.done} / {progress.total}
          </Typography>
        </Box>
      )}

      {/* 一覧テーブル（コホート表示つき） */}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ユーザ</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>コホート</TableCell>
              <TableCell align="right">A（回答）</TableCell>
              <TableCell align="right">B（公開）</TableCell>
              <TableCell align="right">進捗</TableCell>
              <TableCell>バー</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.uid} hover>
                <TableCell>
                  <Typography variant="body2">
                    {r.name}
                    {r.isPurposeTarget && (
                      <Chip size="small" label="目的: 音源視聴&グル活" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{r.uid}</Typography>
                </TableCell>
                <TableCell>{r.email}</TableCell>
                <TableCell>
                  {r.cohort === "new" ? (
                    <Chip size="small" color="success" label="新規" />
                  ) : (
                    <Chip size="small" color="info" label="持ち越し" />
                  )}
                </TableCell>
                <TableCell align="right">{r.A}</TableCell>
                <TableCell align="right">{r.B}</TableCell>
                <TableCell align="right">{(r.ratio * 100).toFixed(1)}%</TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  <LinearProgress variant="determinate" value={Math.min(100, r.ratio * 100)} />
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && !busy && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  結果はまだありません。「集計」を押してください。
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1 }} />
        <Typography variant="caption" color="text.secondary">
          ※ B の取得条件（openDate の型やタイムゾーンなど）は実データに合わせて調整してください。
        </Typography>
      </Box>
    </Box>
  );
};

export default AnswerProgressOnDemandWithCohorts;
